/**
 * EIR-facing notes thread for one founder, scoped to a `page`
 * ("data_room" | "fundraising" | "crm"). Lists that section's notes and posts
 * new ones (optionally emailing the founder). Used inside each EIR section
 * slide panel, so the section is implied by context — no picker needed.
 *
 * Notes are color-coded by author: the current user's own notes read in the
 * program green, everyone else's in a neutral tone (see utils/authorStyle).
 */
import React, { useEffect, useState } from 'react';
import { getEIRNotes, createEIRNote } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { chipFor, initials } from '../utils/authorStyle';

const DEFAULT_TZ = 'America/New_York';

function formatNoteDate(iso) {
  if (!iso) return '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ;
  return new Date(iso).toLocaleString(undefined, {
    timeZone: tz,
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export default function NotesTab({ founderUserId, founderName, page }) {
  const { user } = useAuth();
  const [notes, setNotes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [body, setBody]         = useState('');
  const [notify, setNotify]     = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    setLoading(true);
    getEIRNotes(founderUserId)
      .then(setNotes)
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, [founderUserId]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!body.trim()) return;
    setError('');
    setSaving(true);
    try {
      const note = await createEIRNote(founderUserId, body.trim(), page, notify);
      setNotes((prev) => [note, ...prev]);
      setBody('');
    } catch {
      setError('Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  // Scope to this section (page-less notes show everywhere as general context).
  const pageNotes = page ? notes.filter((n) => !n.page || n.page === page) : notes;

  return (
    <div style={s.wrap}>
      {/* Past notes */}
      <div style={s.notesList}>
        {loading && <p style={s.empty}>Loading…</p>}
        {!loading && pageNotes.length === 0 && (
          <p style={s.empty}>No notes yet. Leave one below.</p>
        )}
        {pageNotes.map((n) => {
          const isMine = user && n.eir_id === user.id;
          const c = chipFor(isMine);
          return (
            <div key={n.id} style={{ ...s.noteCard, borderLeft: `3px solid ${c.border}`, ...(isMine ? s.noteCardMine : {}) }}>
              <div style={s.noteHeader}>
                <span style={{ ...s.avatar, background: c.bg, color: c.fg }}>
                  {initials(n.eir_first_name, n.eir_last_name)}
                </span>
                <span style={s.noteAuthor}>
                  {n.eir_first_name} {n.eir_last_name}{isMine ? ' (you)' : ''}
                </span>
                <span style={s.noteMeta}>{formatNoteDate(n.created_at)}</span>
              </div>
              <p style={s.noteBody}>{n.body}</p>
            </div>
          );
        })}
      </div>

      {/* Compose */}
      <form onSubmit={handleSubmit} style={s.form}>
        <textarea
          style={s.textarea}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Leave a note for ${founderName}…`}
          rows={3}
        />
        <div style={s.formFooter}>
          <label style={s.checkLabel}>
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              style={s.checkbox}
            />
            Notify {founderName} by email
          </label>
          <button type="submit" style={s.submitBtn} disabled={saving || !body.trim()}>
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
        {error && <p style={s.error}>{error}</p>}
      </form>
    </div>
  );
}

const s = {
  wrap:       { display: 'flex', flexDirection: 'column', gap: 16 },
  notesList:  { display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 280, overflowY: 'auto' },
  empty:      { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', opacity: 0.5, textAlign: 'center', padding: '16px 0' },
  noteCard:   { background: 'var(--color-frost)', border: '1px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' },
  noteCardMine: { background: '#fff' },
  noteHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  avatar:     { width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 800, letterSpacing: '0.02em' },
  noteAuthor: { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: 'var(--color-forest)' },
  noteBody:   { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', lineHeight: 1.55, whiteSpace: 'pre-wrap' },
  noteMeta:   { fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--color-forest)', opacity: 0.5, marginLeft: 'auto' },
  form:       { borderTop: '1px solid var(--color-mint-dark)', paddingTop: 14 },
  textarea:   { width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', background: 'var(--color-frost)', border: '1.5px solid var(--color-mint-dark)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', resize: 'vertical', outline: 'none', marginBottom: 10 },
  formFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  checkLabel: { display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-forest)', cursor: 'pointer' },
  checkbox:   { accentColor: 'var(--color-forest)', width: 14, height: 14, cursor: 'pointer' },
  submitBtn:  { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, color: '#fff', background: 'var(--color-forest)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '7px 16px', cursor: 'pointer' },
  error:      { fontFamily: 'var(--font-body)', fontSize: 12, color: '#b71c1c', marginTop: 6 },
};
